import { z } from 'zod';
import type { Schemas } from '../../../api/client';

export type Account = Schemas['Account'];
export type AccountType = Account['type'];
export type AccountRole = NonNullable<Account['role']>;

/** The role each account type can take (ADR-0016): what product categories may post to it. */
export const ROLE_FOR_TYPE: Partial<Record<AccountType, { value: AccountRole; label: string }>> = {
  INCOME: { value: 'SALES', label: 'Sales income of product categories' },
  EXPENSE: { value: 'COGS', label: 'Cost of goods sold of product categories' },
  ASSET: { value: 'INVENTORY', label: 'Stock valuation of product categories' },
};

/** Chip on a non-system account product categories can post to. */
export const ROLE_CHIP: Record<AccountRole, string> = {
  SALES: 'Category sales',
  COGS: 'Category cost',
  INVENTORY: 'Category stock',
};

export const ACCOUNT_TYPES: { value: AccountType; label: string; plural: string; hint: string }[] =
  [
    {
      value: 'ASSET',
      label: 'Asset',
      plural: 'Assets',
      hint: 'What the shop owns: cash, bank, stock, receivables',
    },
    {
      value: 'LIABILITY',
      label: 'Liability',
      plural: 'Liabilities',
      hint: 'What the shop owes: suppliers, taxes, loans',
    },
    {
      value: 'EQUITY',
      label: 'Equity',
      plural: 'Equity',
      hint: 'The owner’s stake and retained earnings',
    },
    { value: 'INCOME', label: 'Income', plural: 'Income', hint: 'Sales and other earnings' },
    {
      value: 'EXPENSE',
      label: 'Expense',
      plural: 'Expenses',
      hint: 'Cost of sales and running costs',
    },
  ];

/** Accounts grouped by type in statement order, each group sorted by code. */
export function groupAccounts(accounts: Account[]): { type: AccountType; accounts: Account[] }[] {
  return ACCOUNT_TYPES.map((t) => ({
    type: t.value,
    accounts: accounts
      .filter((a) => a.type === t.value)
      .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true })),
  })).filter((g) => g.accounts.length > 0);
}

/** Debit-normal for assets and expenses, credit-normal otherwise. */
export function normalSide(type: AccountType): 'DEBIT' | 'CREDIT' {
  return type === 'ASSET' || type === 'EXPENSE' ? 'DEBIT' : 'CREDIT';
}

/**
 * The API balance is debit minus credit. Shown on the account's natural side, so a supplier
 * balance or sales income reads as a positive amount.
 */
export function naturalBalance(a: Pick<Account, 'type' | 'balance'>): number {
  const n = Number(a.balance) || 0;
  const v = normalSide(a.type) === 'DEBIT' ? n : -n;
  return v === 0 ? 0 : v;
}

export const accountFormSchema = z.object({
  code: z
    .string()
    .trim()
    .min(1, 'Enter an account code')
    .max(20, 'At most 20 characters')
    .regex(/^[A-Za-z0-9.-]+$/, 'Letters, digits, dots and dashes only'),
  name: z.string().trim().min(1, 'Enter a name').max(120, 'At most 120 characters'),
  type: z.enum(['ASSET', 'LIABILITY', 'EQUITY', 'INCOME', 'EXPENSE']),
  role: z.enum(['', 'SALES', 'COGS', 'INVENTORY']),
  isPaymentAccount: z.boolean(),
  isActive: z.boolean(),
});

export type AccountFormValues = z.infer<typeof accountFormSchema>;

export function accountDefaults(a?: Account | null): AccountFormValues {
  return a
    ? {
        code: a.code,
        name: a.name,
        type: a.type,
        role: a.role ?? '',
        isPaymentAccount: a.isPaymentAccount,
        isActive: a.isActive,
      }
    : { code: '', name: '', type: 'ASSET', role: '', isPaymentAccount: false, isActive: true };
}

/** The role to send: only one the type allows. */
export function roleOf(v: Pick<AccountFormValues, 'type' | 'role'>): AccountRole | null {
  return v.role && ROLE_FOR_TYPE[v.type]?.value === v.role ? v.role : null;
}

/** Only the fields that changed, plus the version (PATCH). */
export function accountPatch(a: Account, v: AccountFormValues): Schemas['UpdateAccountRequest'] {
  const body: Schemas['UpdateAccountRequest'] = { version: a.version };
  if (v.code.trim() !== a.code) body.code = v.code.trim();
  if (v.name.trim() !== a.name) body.name = v.name.trim();
  if (roleOf(v) !== a.role) body.role = roleOf(v);
  if (v.isPaymentAccount !== a.isPaymentAccount) body.isPaymentAccount = v.isPaymentAccount;
  if (v.isActive !== a.isActive) body.isActive = v.isActive;
  return body;
}

/** "SALES_INVOICE" → "Sales invoice". */
export function sourceLabel(sourceType: string): string {
  const s = sourceType.replace(/_/g, ' ').toLowerCase();
  return s.charAt(0).toUpperCase() + s.slice(1);
}
